import { SKILL_MARKDOWN_PATH } from './skill-markdown';

/**
 * Rules for the paths of files and folders inside a skill bundle, and for the types of the
 * files the editor writes as text.
 *
 * One copy for the editor and the server. They used to each keep their own, and the copies
 * disagreed. A file's type is its stored content type, chosen once when the file is created;
 * its name is only a label, so no rule here looks at an extension.
 */

export const MAX_SKILL_FILE_PATH_CHARS = 255;
export const MAX_SKILL_FOLDER_PATH_CHARS = 200;
/**
 * Content types a skill stores as text. The editor creates Markdown, opens Markdown and plain
 * text in the Markdown editor, and only previews JSON.
 */
export const SKILL_TEXT_CONTENT_TYPES = [
  'text/markdown',
  'text/plain',
  'application/json',
] as const;

export type SkillTextContentType = (typeof SKILL_TEXT_CONTENT_TYPES)[number];

export const SKILL_PATH_KINDS = {
  /** Any file other than SKILL.md. */
  file: 'file',
  folder: 'folder',
} as const;

export type SkillPathKind = (typeof SKILL_PATH_KINDS)[keyof typeof SKILL_PATH_KINDS];

const SKILL_PATH_ERRORS = {
  file: 'Choose a valid file name.',
  folder: 'Choose a valid folder name.',
} satisfies Record<SkillPathKind, string>;

/**
 * Why `path` cannot be used inside a skill bundle, or `null` when it can.
 *
 * Paths are relative and slash-separated: no leading `/`, no backslash, and no empty, `.`
 * or `..` segment, so nothing can escape the bundle or collide with SKILL.md.
 */
export function getSkillPathError(path: string, kind: SkillPathKind): string | null {
  const maxChars =
    kind === SKILL_PATH_KINDS.folder ? MAX_SKILL_FOLDER_PATH_CHARS : MAX_SKILL_FILE_PATH_CHARS;
  const isInvalid =
    !path ||
    path.length > maxChars ||
    path.startsWith('/') ||
    path.includes('\\') ||
    path.split('/').some(segment => !segment || segment === '.' || segment === '..') ||
    (kind === SKILL_PATH_KINDS.file && path.toLowerCase() === SKILL_MARKDOWN_PATH.toLowerCase());
  return isInvalid ? SKILL_PATH_ERRORS[kind] : null;
}

/** Whether a content type, parameters such as `; charset=utf-8` aside, is stored as text. */
export function getIsSkillTextContentType(contentType: string): boolean {
  return Boolean(parseSkillTextContentType(contentType));
}

/** The text type `contentType` names, without parameters, or null for any other. */
export function parseSkillTextContentType(contentType: string): SkillTextContentType | null {
  const mediaType = contentType.split(';')[0]?.trim().toLowerCase() ?? '';
  return SKILL_TEXT_CONTENT_TYPES.find(type => type === mediaType) ?? null;
}
